import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import SmartImage from "@/components/common/SmartImage";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/case-studies/${id}`).then((r) => setCs(r.data)).catch(() => setCs(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">Loading…</div>;
  if (!cs) return <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">Case study not found.</div>;

  return (
    <div data-testid="case-detail" className="bg-[var(--bone)]">
      <section className="relative h-[75vh] overflow-hidden">
        <SmartImage src={cs.cover_image} fallback={IMG.aiPlannerMag} alt={cs.title} className="absolute inset-0 w-full h-full object-cover img-luxe" />
        <div className="absolute inset-0 hero-overlay-strong" />
        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-14 lg:pb-20">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/85 hover:text-[var(--gold-light)] mb-7">
            <ArrowLeft size={12} /> All work
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">{cs.industry}</div>
          <h1 className="mt-4 font-serif-display text-5xl md:text-6xl lg:text-7xl text-white max-w-5xl leading-[0.95]">{cs.title}</h1>
          <div className="mt-5 text-white/85 text-lg">
            {cs.brand} <span className="opacity-50 mx-2">|</span> {cs.cities.join(" · ")} <span className="opacity-50 mx-2">·</span> {cs.duration_weeks} weeks
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--border)]">
        <Metric value={`${cs.reach_millions}M`} label="Estimated reach" />
        <Metric value={`${cs.impressions_millions}M`} label="Impressions" />
        <Metric value={`${cs.engagement_rate}%`} label="Engagement rate" />
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Block label="Objective" body={cs.objective} />
        <Block label="Formats" body={cs.formats.join(" · ")} />
      </section>

      {cs.gallery?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cs.gallery.map((g, i) => (
            <div key={i} className="relative aspect-square overflow-hidden">
              <SmartImage src={g} fallback={IMG.aiPlannerMag} alt="" className="w-full h-full object-cover img-luxe" />
            </div>
          ))}
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Outcome</div>
        <h2 className="mt-4 font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--ink)]">{cs.outcome_headline}</h2>
        <p className="mt-6 text-[var(--slate-1)] text-lg leading-relaxed">{cs.outcome_body}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/ai-planner" className="btn-primary">Plan a similar campaign</Link>
          <Link to="/contact" className="btn-secondary">Talk to us <ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}

function Metric({ value, label }) {
  return (
    <div className="bg-[var(--bone)] p-10 text-center">
      <div className="font-serif-display text-5xl text-[var(--gold)]">{value}</div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{label}</div>
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div className="border border-[var(--border)] p-7">
      <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">{label}</div>
      <div className="mt-3 text-[var(--ink)] text-base leading-relaxed">{body}</div>
    </div>
  );
}
